// main.js
import { createApp } from 'vue';
import App from './App.vue';
import store from './store';
import { i18nState, t, setLocale } from './i18n';
import { formatDateTimeBKK, formatDateBKK } from './utils/datetime';
import { formatFullDate, formatMonthYear, formatShortDate } from './utils/locale';
import { formatMoney, formatBaht, normalizeMoneyInput } from './utils/money';

import 'vuetify/styles';
import { createVuetify } from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { th, en, zhHans, ja, ko } from 'vuetify/locale';
import Vue3Autocounter from 'vue3-autocounter';

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

// iStar new design system. Order matters: design tokens first, then page/layout
// classes, then the seasonal theme overrides (they re-paint the tokens per festival).
import './assets/istar-design.css';
import './assets/istar-pages.css';
import './assets/istar-themes.css';

// iStar shared design components — registered globally so every page can use
// <id-data-grid> / <id-calendar> in its <template> without touching its <script>.
import IdDataGrid from './components/common/IdDataGrid.vue';
import IdCalendar from './components/common/IdCalendar.vue';
import IdPager from './components/common/IdPager.vue';
import IdGridFooter from './components/common/IdGridFooter.vue';
import DuplicateStudentDialog from './components/common/DuplicateStudentDialog.vue';
import IdModal from './components/common/IdModal.vue';
import IdSelect from './components/common/IdSelect.vue';
import IdDateField from './components/common/IdDateField.vue';
import IdMoneyField from './components/common/IdMoneyField.vue';
import SlipVerificationPanel from './components/common/SlipVerificationPanel.vue';
import SlipListPanel from './components/common/SlipListPanel.vue';
import CourseHoldersLine from './components/common/CourseHoldersLine.vue';
import ExpiryRequestDialog from './components/admin/ExpiryRequestDialog.vue';
import ExpiryRequestList from './components/admin/ExpiryRequestList.vue';
import EditHistoryTimeline from './components/common/EditHistoryTimeline.vue';
import CourseUsageTimeline from './components/common/CourseUsageTimeline.vue';
import BookedByCell from './components/common/BookedByCell.vue';
import ThaiAddressFields from './components/common/ThaiAddressFields.vue';
import LangPicker from './components/common/LangPicker.vue';
import hint from './directives/hint';
import clickOutside from './directives/clickOutside';

const vuetify = createVuetify({
  components,
  directives,
  defaultTheme: 'istar',
  locale: {
    locale: localStorage.getItem('istar_locale') || 'th',
    fallback: 'en',
    // our short locale codes double as the Vuetify locale keys (zh → Simplified Chinese)
    messages: { th, en, zh: zhHans, ja, ko },
  },
  themes: {
    // iStar — light pink theme (matches istar-design.css tokens)
    istar: {
      dark: false,
      colors: {
        background: '#fdf2f8',
        surface: '#ffffff',
        'surface-bright': '#ffffff',
        'surface-variant': '#fce7f3',
        'on-surface-variant': '#831843',
        primary: '#ec4899',
        'primary-darken-1': '#db2777',
        secondary: '#8b5cf6',
        error: '#ef4444',
        info: '#3b82f6',
        success: '#10b981',
        warning: '#f59e0b',
        'on-background': '#57323f',
        'on-surface': '#57323f',
        'on-primary': '#ffffff',
      },
    },
    dark: {
      dark: true,
      colors: {
        background: '#121212',
        surface: '#121212',
        primary: '#BB86FC',
        secondary: '#03DAC6',
        error: '#CF6679',
      },
    },
  },
});

const app = createApp(App).component('vue3-autocounter', Vue3Autocounter)

// Restore the session into Vuex on boot. Both halves must go in together — a
// token without userdata leaves `getUserdata` null while `isLoggedIn` is true,
// which is exactly the half-state the auto-login path guards against. The raw
// JSON string is fine to pass: the store parses it (see normalizeAuth), same as
// Login.created() does on refresh.
const token = localStorage.getItem('token');
if (token) {
  store.dispatch('setToken', { token, userdata: localStorage.getItem('userdata') });
}

//console.log("env.SERVER_URL ", env.SERVER_URL);
app.config.globalProperties.baseURL = env.SERVER_URL;
// $minLoad — keep a loading/skeleton state visible for at least `ms` (default 1000ms)
// so fast responses don't make the skeleton flash. Call with the start timestamp:
//   const t0 = Date.now(); ...await fetch...; await this.$minLoad(t0); this.loadingX = false;
app.config.globalProperties.$minLoad = function (startedAt, ms = 1000) {
  const elapsed = Date.now() - startedAt;
  return elapsed >= ms ? Promise.resolve() : new Promise((r) => setTimeout(r, ms - elapsed));
};
// $bustCache — append a timestamp so a re-uploaded image (S3 reuses the same object key,
// so the URL is byte-identical) is re-fetched instead of shown from the browser cache.
//
// EXCEPTION — presigned URLs must be left alone. Slips and profile photos are private objects
// now; the backend hands out short-lived presigned URLs (`X-Amz-Signature=…`). SigV4 signs the
// whole query string, so appending `&t=…` makes the object storage answer
// `403 SignatureDoesNotMatch` and the image breaks. They need no cache-busting anyway: every
// presign carries a fresh `X-Amz-Date`, so each URL is already unique.
app.config.globalProperties.$bustCache = function (url) {
  if (!url) return url;
  if (/[?&]X-Amz-Signature=/.test(url)) return url;
  return url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
};
// single-column sort cycle for server tables (Vuetify v-data-table-server `sort-by` shape):
// none → asc → desc → none. Returns the new sortBy array; $sortDir gives the active column's order.
app.config.globalProperties.$toggleSort = function (current, key) {
  const order = (Array.isArray(current) && current[0] && current[0].key === key) ? current[0].order : null;
  if (order === 'asc') return [{ key, order: 'desc' }];
  if (order === 'desc') return [];
  return [{ key, order: 'asc' }];
};
app.config.globalProperties.$sortDir = function (current, key) {
  return (Array.isArray(current) && current[0] && current[0].key === key) ? current[0].order : '';
};
app.config.globalProperties.$t = t;
app.config.globalProperties.$setLocale = setLocale;
app.config.globalProperties.$i18n = i18nState;
// $formatDateTime / $formatDate — แสดง timestamp จาก backend เป็นเวลาไทย (Asia/Bangkok)
// คงที่ ไม่ขึ้นกับ timezone ของเครื่องผู้ใช้. ใช้ในเทมเพลตได้เลย:
//   {{ $formatDateTime(item.createdate) }}  ->  16/06/2026 18:42
app.config.globalProperties.$formatDateTime = formatDateTimeBKK;
app.config.globalProperties.$formatDate = formatDateBKK;
// locale-aware human dates that follow the selected language (th/en/zh/ja/ko).
// Reactive like $t — templates using these re-render when the user switches language.
//   {{ $formatFullDate(classdate) }}  -> "วันพุธที่ 15 กรกฎาคม 2569" / "2026年7月15日水曜日"
app.config.globalProperties.$formatFullDate = formatFullDate;
app.config.globalProperties.$formatMonthYear = formatMonthYear;
app.config.globalProperties.$formatShortDate = formatShortDate;
// $money / $baht / $moneyInput — จำนวนเงินต้องมีทศนิยม 2 ตำแหน่ง "ทุกจุด" ของระบบ
// (เหตุผล + ข้อยกเว้นเดียวที่ยอมให้ย่อ อยู่ในหัวไฟล์ utils/money.js)
//   {{ $money(row.price) }}      -> 3,901.00
//   {{ $baht(item.amount) }}     -> 3,901.00 ฿
//   @blur="e => form.price = $moneyInput(e.target.value)"   ช่องกรอกเงินทุกช่อง
app.config.globalProperties.$money = formatMoney;
app.config.globalProperties.$baht = formatBaht;
app.config.globalProperties.$moneyInput = normalizeMoneyInput;

app
  .component('VueDatePicker', VueDatePicker)
  .component('IdDataGrid', IdDataGrid)
  .component('IdCalendar', IdCalendar)
  .component('IdPager', IdPager)
  .component('IdGridFooter', IdGridFooter)
  .component('DuplicateStudentDialog', DuplicateStudentDialog)
  .component('IdModal', IdModal)
  .component('IdSelect', IdSelect)
  .component('IdDate', IdDateField)
  .component('IdMoney', IdMoneyField)
  .component('SlipVerificationPanel', SlipVerificationPanel)
  .component('SlipListPanel', SlipListPanel)
  .component('EditHistoryTimeline', EditHistoryTimeline)
  .component('CourseUsageTimeline', CourseUsageTimeline)
  .component('BookedByCell', BookedByCell)
  .component('CourseHoldersLine', CourseHoldersLine)
  .component('ExpiryRequestDialog', ExpiryRequestDialog)
  .component('ExpiryRequestList', ExpiryRequestList)
  .component('ThaiAddressFields', ThaiAddressFields)
  .component('LangPicker', LangPicker)
  .directive('hint', hint)
  .directive('click-outside', clickOutside)
  .use(vuetify)
  .use(store)
  .mount('#app');